/**
 * The model that writes the answers — Explore chats and Create → Q&A: an
 * Azure AI Foundry **connection** and one of its **deployments**. Both lists
 * are server-driven (`GET /ai/chat-models`) — only connections the caller may
 * *use*, and only the deployments each was configured with — so the UI can
 * never offer a model the backend would refuse.
 *
 * `platform` (Q&A only) adds the pinned platform deployment as the first
 * connection option (`PLATFORM_CHOICE_ID`); without it the picker is
 * connection-only, as an Explore chat requires.
 *
 * Retrieval and embeddings are unaffected by this pick: they always run on the
 * platform AI plane. Only the generation calls use the chosen deployment.
 */

import { Alert, Anchor, Loader, Select, Stack, Text } from "@mantine/core";
import { IconPlugConnected } from "@tabler/icons-react";
import { Link as RouterLink } from "react-router-dom";
import type { ChatConnectionOptionOut, PlatformChatModelOut } from "@/shared/api/ai";
import { PLATFORM_CHOICE_ID, type ChatModelChoice } from "./chatModel";

interface ChatModelPickerProps {
  options: ChatConnectionOptionOut[] | null;
  error?: string | null;
  value: ChatModelChoice;
  onChange: (next: ChatModelChoice) => void;
  /** Offer the pinned platform deployment too (Q&A). */
  platform?: PlatformChatModelOut | null;
  /** The note under the selects. */
  footnote?: string;
  disabled?: boolean;
}

const DEFAULT_FOOTNOTE =
  "Searching your collections always runs on the platform; this model only writes the answer.";

type Choices = { value: string; label: string; deployments: { value: string; label: string }[] };

function choicesOf(
  options: ChatConnectionOptionOut[],
  platform: PlatformChatModelOut | null | undefined,
): Choices[] {
  const out: Choices[] = platform
    ? [
        {
          value: PLATFORM_CHOICE_ID,
          label: platform.label,
          deployments: [{ value: platform.deployment, label: platform.deployment }],
        },
      ]
    : [];
  for (const o of options) {
    out.push({
      value: o.connection_id,
      label: o.name,
      deployments: o.deployments.map((d) => ({ value: d.name, label: d.label })),
    });
  }
  return out;
}

export function ChatModelPicker({
  options,
  error,
  value,
  onChange,
  platform,
  footnote = DEFAULT_FOOTNOTE,
  disabled = false,
}: ChatModelPickerProps) {
  if (options === null) return <Loader size="xs" />;
  if (error) {
    return (
      <Alert variant="light" color="red" title="Could not load the AI connections">
        {error}
      </Alert>
    );
  }
  if (options.length === 0 && !platform) {
    return (
      <Alert
        variant="light"
        color="yellow"
        icon={<IconPlugConnected size={18} />}
        title="No Azure AI Foundry connection"
        data-testid="no-ai-connection"
      >
        <Text size="sm">
          A chat answers with a model from an Azure AI Foundry connection you may use. Add one under{" "}
          <Anchor component={RouterLink} to="/integration/connections/new">
            Integrations → Add connection
          </Anchor>
          , or ask its owner to share one with you.
        </Text>
      </Alert>
    );
  }

  const choices = choicesOf(options, platform);
  const selected = choices.find((c) => c.value === value.connectionId);
  return (
    <Stack gap="xs">
      <Select
        label="AI connection"
        description="The Azure AI Foundry connection that writes the answers."
        data={choices.map((c) => ({ value: c.value, label: c.label }))}
        value={value.connectionId}
        onChange={(connectionId) => {
          // Re-source the model list: a deployment from the previous
          // connection does not exist on this endpoint.
          const next = choices.find((c) => c.value === connectionId);
          onChange({ connectionId, deployment: next?.deployments[0]?.value ?? null });
        }}
        allowDeselect={false}
        required
        disabled={disabled}
      />
      <Select
        label="Model"
        data={selected?.deployments ?? []}
        value={value.deployment}
        onChange={(deployment) => onChange({ ...value, deployment })}
        disabled={disabled || !selected || value.connectionId === PLATFORM_CHOICE_ID}
        allowDeselect={false}
        required
      />
      <Text size="xs" c="dimmed">
        {footnote}
      </Text>
    </Stack>
  );
}
